(() => {
  const root = window.Shopify?.routes?.root || '/';
  const readState = element => JSON.parse(element.querySelector('[data-hcbk-state]').textContent);
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  let modal;

  class Product extends HTMLElement {
    connectedCallback() {
      this.connectGallery();
      if (this.ready) return;
      this.ready = true;
      this.state = readState(this);
      this.updateGalleryControls();
      this.addEventListener('change', event => {
        if (event.target.matches('[data-hcbk-option]')) this.changeOption(event.target);
      });
      this.addEventListener('submit', event => this.add(event));
      this.addEventListener('click', event => {
        const thumbnail = event.target.closest('[data-hcbk-thumbnail]');
        if (thumbnail) this.showImage(thumbnail.dataset.image, thumbnail.dataset.imageId);
        const galleryArrow = event.target.closest('[data-hcbk-gallery]');
        if (galleryArrow) this.pageThumbnails(Number(galleryArrow.dataset.hcbkGallery));
        const quantity = event.target.closest('[data-hcbk-quantity]');
        if (quantity) {
          const input = this.querySelector('[name="quantity"]');
          if (Number(quantity.dataset.hcbkQuantity) > 0) input.stepUp(); else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const more = event.target.closest('[data-hcbk-view-more]');
        if (more && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
          event.preventDefault();
          openQuickView(more);
        }
      });
    }

    disconnectedCallback() {
      this.request?.abort();
      this.galleryObserver?.disconnect();
      this.galleryTrack?.removeEventListener('scroll', this.galleryScroll);
    }

    connectGallery() {
      this.galleryTrack = this.querySelector('.hcbk-thumbnails');
      if (!this.galleryTrack) return;
      this.galleryScroll ||= () => this.updateGalleryControls();
      this.galleryTrack.addEventListener('scroll', this.galleryScroll, { passive: true });
      this.galleryObserver ||= new ResizeObserver(this.galleryScroll);
      this.galleryObserver.observe(this.galleryTrack);
      this.updateGalleryControls();
    }

    pageThumbnails(direction) {
      const track = this.galleryTrack;
      if (!track) return;
      const slides = [...track.querySelectorAll('[data-hcbk-thumbnail]')];
      const step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth;
      const page = Math.max(1, Math.round(track.clientWidth / step));
      track.scrollBy({ left: direction * step * page, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    setError(message = '') {
      const error = this.querySelector('.hcbk-error');
      error.textContent = message;
      error.hidden = !message;
    }

    setBusy(busy) {
      this.toggleAttribute('aria-busy', busy);
      this.querySelectorAll('button[type="submit"]').forEach(button => {
        button.disabled = busy || (!this.hasAttribute('data-hcbk-choose-options') && (!this.state.id || !this.state.available));
      });
    }

    showImage(source, id) {
      const image = this.querySelector('.hcbk-main-img, .hcbk-card-image');
      if (image && source) {
        image.removeAttribute('srcset');
        image.src = source;
      }
      this.querySelectorAll('[data-hcbk-thumbnail]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.imageId === String(id))));
      this.updateGalleryControls();
      const selected = this.querySelector('[data-hcbk-thumbnail][aria-pressed="true"]');
      const track = this.querySelector('.hcbk-thumbnails');
      if (selected && track) {
        const rect = selected.getBoundingClientRect();
        const bounds = track.getBoundingClientRect();
        if (rect.left < bounds.left || rect.right > bounds.right) {
          track.scrollBy({ left: rect.left < bounds.left ? rect.left - bounds.left : rect.right - bounds.right, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      }
    }

    updateGalleryControls() {
      const track = this.querySelector('.hcbk-thumbnails');
      if (!track) return;
      const remaining = track.scrollWidth - track.clientWidth - track.scrollLeft;
      this.querySelectorAll('[data-hcbk-gallery]').forEach(button => {
        button.disabled = Number(button.dataset.hcbkGallery) < 0 ? track.scrollLeft <= 1 : remaining <= 1;
      });
    }

    async changeOption(input) {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.setBusy(true);
      this.setError();
      const values = [...this.querySelectorAll('[data-hcbk-option]:checked')].map(option => option.value);
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId || 'hcbk-product-main');
      url.searchParams.set('option_values', values.join(','));
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error(this.state.errorLabel);
        const next = parse(await response.text()).querySelector('hcbk-product');
        if (!next) throw new Error(this.state.errorLabel);
        const state = readState(next);
        const options = next.querySelector('[data-hcbk-options]');
        if (options) {
          options.classList.toggle('hcbk-options--compact', this.matches('hcbk-product-card'));
          options.querySelectorAll('fieldset').forEach(fieldset => {
            fieldset.hidden = this.matches('hcbk-product-card') && !fieldset.classList.contains('hcbk-option--color');
          });
          options.querySelectorAll('[data-hcbk-option]').forEach(option => {
            const position = option.closest('fieldset').dataset.position;
            option.name = `${this.dataset.uid}-option-${position}`;
            option.id = `${this.dataset.uid}-${position}-${option.value}`;
          });
          this.querySelector('[data-hcbk-options]')?.replaceWith(options);
        } else {
          this.querySelector('[data-hcbk-options]')?.remove();
        }
        const specs = next.querySelector('.hcbk-specs');
        if (specs) {
          if (this.matches('hcbk-product-card')) {
            specs.classList.remove('hcbk-specs--expanded');
            specs.querySelectorAll('[data-spec="tires"], [data-spec="max_load"]').forEach(spec => spec.remove());
          }
          this.querySelector('.hcbk-specs')?.replaceWith(specs);
        }
        this.state = state;
        this.querySelector('[data-hcbk-state]').textContent = JSON.stringify(state);
        this.querySelector('[name="id"]').value = state.id || '';
        this.querySelector('[data-hcbk-price]').replaceChildren(...parse(this.matches('hcbk-product-card') ? state.price : state.shortPrice).body.childNodes);
        this.querySelector('.hcbk-price').hidden = !state.id;
        const compare = this.querySelector('[data-hcbk-compare]');
        compare.replaceChildren(...parse(state.compare).body.childNodes);
        compare.hidden = !state.compare;
        this.querySelector('[data-hcbk-sale]').hidden = !state.saving;
        this.querySelector('[data-hcbk-saving]').replaceChildren(...parse(state.saving).body.childNodes);
        this.querySelector('[data-hcbk-add-label]').textContent = this.hasAttribute('data-hcbk-choose-options') ? state.chooseLabel : !state.id ? state.unavailableLabel : state.available ? state.addLabel : state.soldLabel;
        const selectedUrl = new URL(state.url, location.origin);
        if (!state.id) selectedUrl.searchParams.set('option_values', values.join(','));
        this.querySelectorAll('[data-hcbk-link], [data-hcbk-view-more]').forEach(link => link.href = selectedUrl.href);
        const quantity = this.querySelector('input[name="quantity"]');
        const nextQuantity = next.querySelector('input[name="quantity"]');
        if (quantity?.type === 'number' && nextQuantity) {
          ['min', 'max', 'step'].forEach(attr => {
            if (nextQuantity.hasAttribute(attr)) quantity.setAttribute(attr, nextQuantity.getAttribute(attr)); else quantity.removeAttribute(attr);
          });
          if (!quantity.checkValidity()) quantity.value = nextQuantity.value;
        }
        this.showImage(state.image, state.imageId);
        if (this.matches('hcbk-product') && !this.closest('dialog')) {
          const current = new URL(location.href);
          if (state.id) {
            current.searchParams.set('variant', state.id);
            current.searchParams.delete('option_values');
          } else {
            current.searchParams.delete('variant');
            current.searchParams.set('option_values', values.join(','));
          }
          history.replaceState({}, '', current);
        }
        this.querySelector(`[data-hcbk-option][value="${input.value}"]`)?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.setError(error.message || this.state.errorLabel);
          // Never allow adding a previous variant after a failed selection request.
          this.state.id = null;
          this.querySelector('[name="id"]').value = '';
        }
      } finally {
        if (this.request === request) this.setBusy(false);
      }
    }

    async add(event) {
      const form = event.target;
      if (!form.matches('.hcbk-product-form, .hcbk-add-form')) return;
      event.preventDefault();
      if (this.hasAttribute('aria-busy')) return;
      if (this.hasAttribute('data-hcbk-choose-options')) {
        openQuickView(event.submitter, this.querySelector('[data-hcbk-view-more]').href);
        return;
      }
      if (!this.state.id || !this.state.available || this.hasAttribute('aria-busy')) return;
      const buyNow = event.submitter?.name === 'checkout';
      const trigger = event.submitter;
      const drawer = document.querySelector('cart-drawer');
      this.setBusy(true);
      this.setError();
      try {
        const data = new FormData(form);
        if (drawer) {
          data.set('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
          data.set('sections_url', location.pathname);
        }
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.state.errorLabel);
        if (buyNow) { location.assign(`${root}checkout`); return; }
        if (drawer && result.sections?.['cart-drawer']) {
          const returnFocus = modal?.open ? modal.trigger : trigger;
          if (modal?.open) modal.close();
          drawer.classList.remove('is-empty');
          drawer.setActiveElement(returnFocus);
          drawer.renderContents(result);
        } else { location.assign(`${root}cart`); }
      } catch (error) {
        this.setError(error.message || this.state.errorLabel);
      } finally { this.setBusy(false); }
    }
  }

  async function openQuickView(trigger, productUrl = trigger.href) {
    if (!modal) {
      modal = document.createElement('dialog');
      modal.className = 'hcbk-quick-view';
      modal.setAttribute('aria-label', window.hcbkStrings.details);
      const header = document.createElement('div');
      header.className = 'hcbk-quick-view-header';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'hcbk-quick-view-close';
      close.textContent = '×';
      close.setAttribute('aria-label', window.hcbkStrings.close);
      close.addEventListener('click', () => modal.close());
      header.append(close);
      const content = document.createElement('div');
      content.className = 'hcbk-quick-view-content';
      modal.append(header, content);
      document.body.append(modal);
      modal.addEventListener('click', event => {
        const rect = modal.getBoundingClientRect();
        if (event.target === modal && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) modal.close();
      });
      modal.addEventListener('close', () => {
        modal.request?.abort();
        modal.querySelectorAll('video').forEach(video => video.pause());
        document.body.style.overflow = modal.previousOverflow || '';
        modal.trigger?.focus({ preventScroll: true });
      });
    }
    modal.request?.abort();
    modal.request = new AbortController();
    modal.trigger = trigger;
    modal.previousOverflow = document.body.style.overflow;
    const content = modal.querySelector('.hcbk-quick-view-content');
    content.textContent = window.hcbkStrings.loading;
    modal.showModal();
    document.body.style.overflow = 'hidden';
    modal.scrollTop = 0;
    const url = new URL(productUrl);
    url.searchParams.set('section_id', 'hcbk-product-main');
    try {
      const response = await fetch(url, { signal: modal.request.signal });
      if (!response.ok) throw new Error('Product unavailable');
      const product = parse(await response.text()).querySelector('hcbk-product');
      if (!product) throw new Error('Product unavailable');
      const uid = `quick-${product.dataset.uid}`;
      product.dataset.uid = uid;
      product.querySelectorAll('[id]').forEach(element => element.id = `quick-${element.id}`);
      product.querySelectorAll('[for]').forEach(element => element.htmlFor = `quick-${element.htmlFor}`);
      product.querySelectorAll('[data-hcbk-option]').forEach(option => option.name = `quick-${option.name}`);
      product.querySelector('.hcbk-main-img')?.setAttribute('fetchpriority', 'auto');
      content.replaceChildren(product);
      modal.setAttribute('aria-label', readState(product).title);
    } catch (error) {
      if (error.name !== 'AbortError') {
        content.textContent = window.hcbkStrings.error + ' ';
        const link = document.createElement('a');
        link.href = productUrl;
        link.textContent = window.hcbkStrings.details;
        content.append(link);
      }
    }
  }
  if (!customElements.get('hcbk-product')) customElements.define('hcbk-product', Product);
  if (!customElements.get('hcbk-product-card')) customElements.define('hcbk-product-card', class extends Product {});
})();
